import { Loader2, Upload } from "lucide-react"
import { useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useUploadDocument } from "@/features/documents/hooks/use-document-mutations"
import type { UploadDocumentInput } from "@/features/documents/lib/upload-helpers"

interface DocumentUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  level: UploadDocumentInput["level"]
  contractId?: string
  orderId?: string
  deliveryId?: string
  invalidateKeys?: readonly (readonly unknown[])[]
}

export function DocumentUploadDialog({
  open,
  onOpenChange,
  level,
  contractId,
  orderId,
  deliveryId,
  invalidateKeys,
}: DocumentUploadDialogProps) {
  const { t } = useTranslation(["documents", "common"])
  const fileInputId = useId()
  const titleId = useId()
  const descId = useId()
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [error, setError] = useState<string | null>(null)

  const upload = useUploadDocument({
    invalidateKeys,
    onSuccess: () => {
      setFile(null)
      setTitle("")
      setDescription("")
      if (fileRef.current) fileRef.current.value = ""
      onOpenChange(false)
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!file) {
      setError(t("errors.noFile"))
      return
    }
    upload.mutate({
      file,
      title: title.trim() || file.name,
      description: description.trim() || undefined,
      level,
      contractId,
      orderId,
      deliveryId,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("form.title")}</DialogTitle>
          <DialogDescription>{t("form.fields.fileHint")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={fileInputId}>{t("form.fields.file")}</Label>
            <Input
              ref={fileRef}
              id={fileInputId}
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {error ? (
              <p className="text-destructive text-xs">{error}</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={titleId}>{t("form.fields.title")}</Label>
            <Input
              id={titleId}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={file?.name ?? ""}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={descId}>{t("form.fields.description")}</Label>
            <Textarea
              id={descId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={upload.isPending}
            >
              {t("actions.cancel", { ns: "common" })}
            </Button>
            <Button type="submit" disabled={upload.isPending}>
              {upload.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t("actions.upload", { ns: "common" })}
                </>
              ) : (
                <>
                  <Upload className="size-4" />
                  {t("form.submit")}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
